// SYNC SERVICE
import { Injectable, inject, signal } from "@angular/core";
import { DataService } from "./data.service";
import { BehaviorSubject, firstValueFrom, forkJoin } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { db, dbSwitching$ } from "src/app/db.service";
import * as _moment from "moment";
import { AuthService } from "./auth.service";
const moment = _moment;

@Injectable({
  providedIn: "root",
})
export class SyncService {
  _dataService = inject(DataService);
  _authService = inject(AuthService);
  http = inject(HttpClient);

  loadingProgress = 0;
  dbSyncing = false;
  gettingSettings = false;
  showSyncLine = true;
  syncDate =
    this._dataService.getLS("syncDate") ||
    moment(new Date()).format("HH:mm DD/MM/YY");
  newDataSynced: BehaviorSubject<string> = new BehaviorSubject(this.syncDate);
  
  // Track pending SYNC_SUCCESS operations to prevent race conditions
  pendingSyncOperations = new Set<string>();

  lastUpdateTimeLocal: {
    bank?: Date;
    bankGroups?: Date;
    cashBox?: Date;
    customerGroups?: Date;
    customers?: Date;
    formulaGroups?: Date;
    formulas?: Date;
    materials?: Date;
    personnel?: Date;
    production?: Date;
    products?: Date;
    sells?: Date;
    stock?: Date;
    stockGroups?: Date;
    transactionDeletions?: Date;
    transactions?: Date;
    users?: Date;
  } = this._dataService.getLS("lastUpdateTimeLocal") || {
    bank: new Date(),
    bankGroups: new Date(),
    cashBox: new Date(),
    customerGroups: new Date(),
    customers: new Date(),
    formulaGroups: new Date(),
    formulas: new Date(),
    materials: new Date(),
    personnel: new Date(),
    production: new Date(),
    products: new Date(),
    sells: new Date(),
    stock: new Date(),
    stockGroups: new Date(),
    transactionDeletions: new Date(),
    transactions: new Date(),
    users: new Date(),
  };

  async syncDB() {
    if (this.dbSyncing || !this._authService.loggedInUserValue) {
      return;
    }
    
    // Check if database is currently switching
    if (dbSwitching$.getValue()) {
      console.log("Database is switching, skipping sync");
      return;
    }
    
    // Wait for pending SYNC_SUCCESS operations to complete
    if (this.pendingSyncOperations.size > 0) {
      console.log(`Waiting for ${this.pendingSyncOperations.size} pending sync operations to complete...`);
      const maxWaitTime = 5000; // 5 seconds max wait
      const startTime = Date.now();
      
      while (this.pendingSyncOperations.size > 0 && (Date.now() - startTime) < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      if (this.pendingSyncOperations.size > 0) {
        console.warn(`Still ${this.pendingSyncOperations.size} pending operations after ${maxWaitTime}ms, proceeding with sync anyway`);
      } else {
        console.log("All pending operations completed, proceeding with sync");
      }
    }
    
    let newDataCommittedThisSync = false;
    try {
      const progressUnit = 10;
      this.dbSyncing = true;
      this.showSyncLine = true;
      this.loadingProgress = progressUnit;

      const lastId: any = {
        transaction: this._dataService.getLS("lastTransactionId") || 0,
        delete: this._dataService.getLS("lastTransactionDeletion") || 0,
        bank: this._dataService.getLS("lastBankId") || 0,
        sells: this._dataService.getLS("lastSellId") || 0,
        stock: this._dataService.getLS("lastStockId") || 0,
        production: this._dataService.getLS("lastProductionId") || 0,
      };

      const [
        transactionsResult,
        transactionDeletionsResult,
        bankResult,
        sellsResult,
        stocksResult,
        productionResult,
      ] = await Promise.all([
        firstValueFrom(
          this.http.get<any>(
            `https://tekoplast.az/jjapi/api.php/records/transactions?filter=id,gt,${lastId.transaction}`
          )
        ),
        firstValueFrom(
          this.http.get<any>(
            `https://tekoplast.az/jjapi/api.php/records/transactionDeletions?filter=id,gt,${lastId.delete}`
          )
        ),
        firstValueFrom(
          this.http.get<any>(
            `https://tekoplast.az/jjapi/api.php/records/bank?join=transactions&filter=id,gt,${lastId.bank}`
          )
        ),
        firstValueFrom(
          this.http.get<any>(
            `https://tekoplast.az/jjapi/api.php/records/sells?join=transactions&filter=id,gt,${lastId.sells}`
          )
        ),
        firstValueFrom(
          this.http.get<any>(
            `https://tekoplast.az/jjapi/api.php/records/stock?join=transactions&filter=id,gt,${lastId.stock}`
          )
        ),
        firstValueFrom(
          this.http.get<any>(
            `https://tekoplast.az/jjapi/api.php/records/production?join=transactions&filter=id,gt,${lastId.production}`
          )
        ),
      ]);

      this.loadingProgress += progressUnit;

      const transactions: any[] = transactionsResult.records;
      const bank: any[] = bankResult.records;
      const transactionDeletions: any[] = transactionDeletionsResult.records;
      const sells: any[] = sellsResult.records;
      const stocks: any[] = stocksResult.records;
      const production: any[] = productionResult.records;

      const lastIdSrv: any = {
        transaction:
          transactions.length > 0
            ? Math.max(...transactions.map((item) => item.id))
            : lastId.transaction,
        delete:
          transactionDeletions.length > 0
            ? Math.max(...transactionDeletions.map((item) => item.id))
            : lastId.delete,
        bank:
          bank.length > 0
            ? Math.max(...bank.map((item) => item.id))
            : lastId.bank,
        sells:
          sells.length > 0
            ? Math.max(...sells.map((item) => item.id))
            : lastId.sells,
        stock:
          stocks.length > 0
            ? Math.max(...stocks.map((item) => item.id))
            : lastId.stock,
        production:
          production.length > 0
            ? Math.max(...production.map((item) => item.id))
            : lastId.production,
      };
      this.loadingProgress += progressUnit;

      if (!this.lastUpdateTimeLocal) {
        this.lastUpdateTimeLocal = {} as any;
      }

      const dataAvailable =
        transactions.length > 0 ||
        bank.length > 0 ||
        sells.length > 0 ||
        stocks.length > 0 ||
        production.length > 0 ||
        transactionDeletions.length > 0;

      if (dataAvailable) {
        // --- START OF ATOMIC TRANSACTION ---
        // All database operations are now wrapped here
        await db.transaction(
          "rw",
          db.transactions,
          db.bank,
          db.sells,
          db.stock,
          db.production,
          async () => {
            // ADD NEW TRANSACTIONS
            if (transactions.length > 0) {
              await db.transactions.bulkPut(transactions);
              console.log(`added ${transactions.length} transactions`);
              this.updateLastUpdateTime("transactions");
            }
            this.loadingProgress += progressUnit;

            // Insert new bank
            if (bank.length > 0) {
              const newBankRecords = bank.map((b: any) => ({
                ...b,
                tId: b.transactionId.id,
                date: b.transactionId.date,
                customerId: b.customerId
                  ? b.customerId
                  : b.transactionId.customerId,
              }));
              await db.bank.bulkPut(newBankRecords);
              console.log(`added ${newBankRecords.length} bank`);
              this.updateLastUpdateTime("bank");
            }
            this.loadingProgress += progressUnit;

            // Insert new sells
            if (sells.length > 0) {
              const newSells = sells.map((sell: any) => ({
                ...sell,
                tId: sell.transactionId.id,
                date: sell.transactionId.date,
              }));
              await db.sells.bulkPut(newSells);
              console.log(`added ${newSells.length} sells`);
              this.updateLastUpdateTime("sells");
            }
            this.loadingProgress += progressUnit;

            // Insert new production
            if (production.length > 0) {
              const newProduction = production.map((pr: any) => ({
                ...pr,
                tId: pr.transactionId.id,
                date: pr.transactionId.date,
              }));
              await db.production.bulkPut(newProduction);
              console.log(`added ${newProduction.length} production`);
              this.updateLastUpdateTime("production");
            }
            this.loadingProgress += progressUnit;

            // Insert new stocks
            if (stocks.length > 0) {
              const newStocks = stocks
                .map((st: any) => ({
                  ...st,
                  tId: st.transactionId.id,
                  date: st.transactionId.date,
                  customerId: st.transactionId.customerId,
                  tType: st.transactionId.type,
                }));
              
              await db.stock.bulkPut(newStocks);
              console.log(`added ${newStocks.length} stocks`);
              this.updateLastUpdateTime("stock");
            }
            this.loadingProgress += progressUnit;

            // HANDLE DELETIONS
            if (transactionDeletions.length > 0) {
              await this.handleTransactionDeletions(transactionDeletions);
              this.updateLastUpdateTime("transactions"); // Deletions also affect transactions
            }
          }
        );
        // --- END OF ATOMIC TRANSACTION ---

        // This block now only runs if the entire transaction above was successful
        newDataCommittedThisSync = true;

        // === Set Last IDs in localStorage after successful DB operations ===
        if (transactions.length > 0)
          this._dataService.setLS("lastTransactionId", lastIdSrv.transaction);
        if (bank.length > 0)
          this._dataService.setLS("lastBankId", lastIdSrv.bank);
        if (sells.length > 0)
          this._dataService.setLS("lastSellId", lastIdSrv.sells);
        if (production.length > 0)
          this._dataService.setLS("lastProductionId", lastIdSrv.production);
        if (stocks.length > 0)
          this._dataService.setLS("lastStockId", lastIdSrv.stock);
        if (transactionDeletions.length > 0)
          this._dataService.setLS("lastTransactionDeletion", lastIdSrv.delete);

        this._dataService.setLS(
          "lastUpdateTimeLocal",
          this.lastUpdateTimeLocal
        );
      }

      this.loadingProgress =
        this.loadingProgress < 90 ? 90 : Math.min(this.loadingProgress, 100);

      setTimeout(() => {
        this.dbSyncing = false;
        this.showSyncLine = false;
        this.loadingProgress = 0;
      }, 500);

      this.syncDate = moment(new Date()).format("HH:mm DD/MM/YY");
      this._dataService.setLS("syncDate", this.syncDate);

      if (newDataCommittedThisSync) {
        this.newDataSynced.next(this.syncDate);
      }
      console.log(`Sync Completed: ${this.syncDate}`);
    } catch (error) {
      // If the transaction fails, the DB state is preserved.
      console.error(
        "Sync transaction failed. Local database state was not changed.",
        error
      );
      this.dbSyncing = false;
      this.showSyncLine = false;
      this.loadingProgress = 0;
    }
  }

  /**
   * Enhanced deletion handler with proper transaction management
   */
  async handleTransactionDeletions(transactionDeletions: any[]) {
    const deletedTransactionIds = transactionDeletions.map(
      (td: any) => td.transactionId
    );

    console.log(
      `Processing ${deletedTransactionIds.length} transaction deletions:`,
      deletedTransactionIds
    );

    // Use a single Dexie transaction to ensure atomicity
    await db.transaction(
      "rw",
      db.transactions,
      db.sells,
      db.bank,
      db.stock,
      db.production,
      async () => {
        // First, find all related records BEFORE deleting anything
        const [
          deletedSellIds,
          deletedBankIds,
          deletedStockIds,
          deletedProductionIds,
        ] = await Promise.all([
          db.sells.where("tId").anyOf(deletedTransactionIds).primaryKeys(),
          db.bank.where("tId").anyOf(deletedTransactionIds).primaryKeys(),
          db.stock.where("tId").anyOf(deletedTransactionIds).primaryKeys(),
          db.production.where("tId").anyOf(deletedTransactionIds).primaryKeys(),
        ]);

        // Validate that we found the expected relationships
        const transactionsToDelete = await db.transactions
          .where("id")
          .anyOf(deletedTransactionIds)
          .toArray();

        // Delete all related records first (to maintain referential integrity)
        const deletionPromises = [];

        if (deletedSellIds.length > 0) {
          deletionPromises.push(
            db.sells.bulkDelete(deletedSellIds).then(() => {
              console.log(`Deleted ${deletedSellIds.length} sell records`);
              this.updateLastUpdateTime("sells");
            })
          );
        }

        if (deletedBankIds.length > 0) {
          deletionPromises.push(
            db.bank.bulkDelete(deletedBankIds).then(() => {
              console.log(`Deleted ${deletedBankIds.length} bank records`);
              this.updateLastUpdateTime("bank");
            })
          );
        }

        if (deletedStockIds.length > 0) {
          deletionPromises.push(
            db.stock.bulkDelete(deletedStockIds).then(() => {
              console.log(`Deleted ${deletedStockIds.length} stock records`);
              this.updateLastUpdateTime("stock");
            })
          );
        }

        if (deletedProductionIds.length > 0) {
          deletionPromises.push(
            db.production.bulkDelete(deletedProductionIds).then(() => {
              console.log(
                `Deleted ${deletedProductionIds.length} production records`
              );
              this.updateLastUpdateTime("production");
            })
          );
        }

        // Execute all related record deletions
        await Promise.all(deletionPromises);

        // Finally, delete the transactions
        await db.transactions.bulkDelete(deletedTransactionIds);
        console.log(
          `Deleted ${deletedTransactionIds.length} transaction records`
        );

        this.loadingProgress += 10;
      }
    );
  }

  /**
   * Helper method to update last update times
   */
  private updateLastUpdateTime(tableName: string) {
    if (
      tableName in this.lastUpdateTimeLocal ||
      this.lastUpdateTimeLocal.hasOwnProperty(tableName) ||
      typeof this.lastUpdateTimeLocal[tableName] !== "undefined"
    ) {
      this.lastUpdateTimeLocal[tableName] = new Date();
    }

    // Special handling for stock affecting materials
    if (tableName === "stock") {
      if (
        "materials" in this.lastUpdateTimeLocal ||
        this.lastUpdateTimeLocal.hasOwnProperty("materials") ||
        typeof this.lastUpdateTimeLocal.materials !== "undefined"
      ) {
        this.lastUpdateTimeLocal.materials = new Date();
      }
    }
  }

  /**
   * Enhanced deleteItems method with better error handling and logging
   */
  async deleteItems(ids: any[], collectionName: string) {
    if (ids.length > 0) {
      try {
        await db[collectionName].bulkDelete(ids);
        console.log(
          `Successfully deleted ${ids.length} ${collectionName} records`
        );
      } catch (error) {
        console.error(`Failed to delete ${collectionName} records:`, error);
        // Log which specific IDs failed
        console.error(`Failed IDs for ${collectionName}:`, ids);
        throw error; // Re-throw to be handled by the transaction wrapper
      }
    }
  }

  /**
   * Enhanced data integrity check method - can be called periodically
   */
  async checkDataIntegrity(): Promise<{
    orphanedRecords: {
      bank: any[];
      sells: any[];
      stock: any[];
      production: any[];
    };
    missingTransactions: any[];
    summary: {
      totalTransactions: number;
      totalBankRecords: number;
      totalSellRecords: number;
      totalStockRecords: number;
      totalProductionRecords: number;
      orphanedBankCount: number;
      orphanedSellsCount: number;
      orphanedStockCount: number;
      orphanedProductionCount: number;
      missingTransactionsCount: number;
    };
  }> {
    try {
      // Get all transaction IDs (handles both numeric and UUID formats)
      const allTransactions = await db.transactions.toArray();
      const transactionIdSet = new Set(allTransactions.map((t) => t.id));

      // Get all related records
      const [bankRecords, sellRecords, stockRecords, productionRecords] =
        await Promise.all([
          db.bank.toArray(),
          db.sells.toArray(),
          db.stock.toArray(),
          db.production.toArray(),
        ]);

      // Find orphaned records - records that reference non-existent transactions
      const orphanedRecords = {
        bank: bankRecords.filter((record) => {
          const hasTransaction = transactionIdSet.has(record.tId);
          if (!hasTransaction && bankRecords.indexOf(record) < 3) {
            console.log(
              `Orphaned bank record: ID=${record.id}, tId=${record.tId}, customerName=${record.customerName}`
            );
          }
          return !hasTransaction;
        }),
        sells: sellRecords.filter((record) => {
          const hasTransaction = transactionIdSet.has(record.tId);
          if (!hasTransaction && sellRecords.indexOf(record) < 3) {
            console.log(
              `Orphaned sell record: ID=${record.id}, tId=${record.tId}`
            );
          }
          return !hasTransaction;
        }),
        stock: stockRecords.filter((record) => {
          const hasTransaction = transactionIdSet.has(record.tId);
          if (!hasTransaction && stockRecords.indexOf(record) < 3) {
            console.log(
              `Orphaned stock record: ID=${record.id}, tId=${record.tId}`
            );
          }
          return !hasTransaction;
        }),
        production: productionRecords.filter((record) => {
          const hasTransaction = transactionIdSet.has(record.tId);
          if (!hasTransaction && productionRecords.indexOf(record) < 3) {
            console.log(
              `Orphaned production record: ID=${record.id}, tId=${record.tId}`
            );
          }
          return !hasTransaction;
        }),
      };

      // Find missing transactions (referenced by child records but don't exist)
      const referencedTransactionIds = new Set([
        ...bankRecords.map((r) => r.tId).filter((tId) => tId != null),
        ...sellRecords.map((r) => r.tId).filter((tId) => tId != null),
        ...stockRecords.map((r) => r.tId).filter((tId) => tId != null),
        ...productionRecords.map((r) => r.tId).filter((tId) => tId != null),
      ]);

      const missingTransactions = Array.from(referencedTransactionIds)
        .filter((id) => !transactionIdSet.has(id))
        .map((id) => ({
          id,
          referencedBy: {
            bank: bankRecords.filter((r) => r.tId === id).length,
            sells: sellRecords.filter((r) => r.tId === id).length,
            stock: stockRecords.filter((r) => r.tId === id).length,
            production: productionRecords.filter((r) => r.tId === id).length,
          },
        }));

      const summary = {
        totalTransactions: allTransactions.length,
        totalBankRecords: bankRecords.length,
        totalSellRecords: sellRecords.length,
        totalStockRecords: stockRecords.length,
        totalProductionRecords: productionRecords.length,
        orphanedBankCount: orphanedRecords.bank.length,
        orphanedSellsCount: orphanedRecords.sells.length,
        orphanedStockCount: orphanedRecords.stock.length,
        orphanedProductionCount: orphanedRecords.production.length,
        missingTransactionsCount: missingTransactions.length,
      };

      return { orphanedRecords, missingTransactions, summary };
    } catch (error) {
      console.error("Error during data integrity check:", error);
      throw error;
    }
  }

  /**
   * Enhanced cleanup method with better error handling and reporting
   */
  async cleanupOrphanedRecords(): Promise<{
    cleaned: {
      bank: number;
      sells: number;
      stock: number;
      production: number;
    };
    errors: any[];
  }> {
    const { orphanedRecords, summary } = await this.checkDataIntegrity();

    if (
      summary.orphanedBankCount === 0 &&
      summary.orphanedSellsCount === 0 &&
      summary.orphanedStockCount === 0 &&
      summary.orphanedProductionCount === 0
    ) {
      return {
        cleaned: { bank: 0, sells: 0, stock: 0, production: 0 },
        errors: [],
      };
    }

    const cleaned = { bank: 0, sells: 0, stock: 0, production: 0 };
    const errors: any[] = [];

    try {
      await db.transaction(
        "rw",
        db.bank,
        db.sells,
        db.stock,
        db.production,
        async () => {
          // Clean up orphaned bank records
          if (orphanedRecords.bank.length > 0) {
            try {
              const orphanedBankIds = orphanedRecords.bank.map((r) => r.id);

              const deletedCount = await db.bank.bulkDelete(orphanedBankIds);
              cleaned.bank = orphanedBankIds.length;
            } catch (error) {
              errors.push({ type: "bank", error: error.message });
            }
          }

          // Clean up orphaned sell records
          if (orphanedRecords.sells.length > 0) {
            try {
              const orphanedSellIds = orphanedRecords.sells.map((r) => r.id);

              await db.sells.bulkDelete(orphanedSellIds);
              cleaned.sells = orphanedSellIds.length;
            } catch (error) {
              console.error("Error cleaning sell records:", error);
              errors.push({ type: "sells", error: error.message });
            }
          }

          // Clean up orphaned stock records
          if (orphanedRecords.stock.length > 0) {
            try {
              const orphanedStockIds = orphanedRecords.stock.map((r) => r.id);
              console.log(
                `Cleaning up ${orphanedStockIds.length} orphaned stock records...`
              );

              await db.stock.bulkDelete(orphanedStockIds);
              cleaned.stock = orphanedStockIds.length;
              console.log(
                `✓ Cleaned up ${cleaned.stock} orphaned stock records`
              );
            } catch (error) {
              console.error("Error cleaning stock records:", error);
              errors.push({ type: "stock", error: error.message });
            }
          }

          // Clean up orphaned production records
          if (orphanedRecords.production.length > 0) {
            try {
              const orphanedProductionIds = orphanedRecords.production.map(
                (r) => r.id
              );
              console.log(
                `Cleaning up ${orphanedProductionIds.length} orphaned production records...`
              );

              await db.production.bulkDelete(orphanedProductionIds);
              cleaned.production = orphanedProductionIds.length;
              console.log(
                `✓ Cleaned up ${cleaned.production} orphaned production records`
              );
            } catch (error) {
              console.error("Error cleaning production records:", error);
              errors.push({ type: "production", error: error.message });
            }
          }
        }
      );

      const totalCleaned =
        cleaned.bank + cleaned.sells + cleaned.stock + cleaned.production;
      console.log(`=== CLEANUP COMPLETE ===`);
      console.log(`Total records cleaned: ${totalCleaned}`);
      console.log("Breakdown:", cleaned);

      if (errors.length > 0) {
        console.log("Errors encountered:", errors);
      }

      return { cleaned, errors };
    } catch (error) {
      console.error("Critical error during cleanup transaction:", error);
      errors.push({ type: "transaction", error: error.message });
      return { cleaned, errors };
    }
  }

  /**
   * Helper method to check a specific transaction's relationships
   */
  async debugTransactionRelationships(transactionId: any) {
    try {
      const transaction = await db.transactions.get(transactionId);
      if (transaction) {
        console.log("Transaction details:", {
          id: transaction.id,
          type: transaction.type,
          date: transaction.date,
          customerId: transaction.customerId,
        });
      }

      const [bankEntries, sellEntries, stockEntries, productionEntries] =
        await Promise.all([
          db.bank.where("tId").equals(transactionId).toArray(),
          db.sells.where("tId").equals(transactionId).toArray(),
          db.stock.where("tId").equals(transactionId).toArray(),
          db.production.where("tId").equals(transactionId).toArray(),
        ]);

      console.log("Related records:", {
        bank: bankEntries.length,
        sells: sellEntries.length,
        stock: stockEntries.length,
        production: productionEntries.length,
      });

      // Check if any records exist but transaction doesn't
      const totalRelatedRecords =
        bankEntries.length +
        sellEntries.length +
        stockEntries.length +
        productionEntries.length;
    } catch (error) {
      console.error("Error in debug check:", error);
    }
  }

  /**
   * Quick method to get data integrity stats without full check
   */
  async getDataIntegrityStats() {
    const [
      transactionCount,
      bankCount,
      sellsCount,
      stockCount,
      productionCount,
    ] = await Promise.all([
      db.transactions.count(),
      db.bank.count(),
      db.sells.count(),
      db.stock.count(),
      db.production.count(),
    ]);

    return {
      transactions: transactionCount,
      bank: bankCount,
      sells: sellsCount,
      stock: stockCount,
      production: productionCount,
      total: bankCount + sellsCount + stockCount + productionCount,
    };
  }

  reset() {
    this.showSyncLine = true;
    db.transactions.clear();
    db.bank.clear();
    db.sells.clear();
    db.stock.clear();
    db.production.clear();

    localStorage.removeItem(this._dataService.dbTable + "lastTransactionId");
    localStorage.removeItem(`${this._dataService.dbTable}lastBankId`);
    localStorage.removeItem(`${this._dataService.dbTable}lastSellId`);
    localStorage.removeItem(`${this._dataService.dbTable}lastStockId`);
    localStorage.removeItem(`${this._dataService.dbTable}lastProductionId`);
    localStorage.removeItem(
      `${this._dataService.dbTable}lastTransactionDeletion`
    );
    this.syncDB();
  }

  getLSData() {
    this._dataService.customers.set(this._dataService.getLS("customers") || []);
    this._dataService.customerGroups.set(
      this._dataService.getLS("customerGroups") || []
    );
    this._dataService.products.set(this._dataService.getLS("products") || []);
    this._dataService.bankGroups.set(
      this._dataService.getLS("bankGroups") || []
    );
    this._dataService.materials.set(this._dataService.getLS("materials") || []);
    this._dataService.stockGroups.set(
      this._dataService.getLS("stockGroups") || []
    );
    this._dataService.formulaGroups.set(
      this._dataService.getLS("formulaGroups") || []
    );
    this._dataService.cashBox.set(this._dataService.getLS("cashBox") || []);
  }

  getSettings() {
    if (this.gettingSettings) return;
    this.gettingSettings = true;
    forkJoin([
      this._dataService.getFormulaGroups(),
      this._dataService.getCustomers(),
      this._dataService.getMaterials(),
      this._dataService.getStockGroups(),
      this._dataService.getProducts(),
      this._dataService.getBankGroups(),
      this._dataService.getCashBox(),
      this._dataService.getCustomerGroups(),
      this._dataService.getTodos(),
      this._dataService.getUsers(),
      this._dataService.getFormulas(),
    ]).subscribe({
      next: (r) => {
        localStorage.setItem("formulaGroups", JSON.stringify(r[0]));
        this._dataService.setLS("formulaGroups", r[0]);
        this._dataService.formulaGroups.set(r[0]);

        localStorage.setItem("customers", JSON.stringify(r[1]));
        this._dataService.setLS("customers", r[1]);
        this._dataService.customers.set(r[1]);

        localStorage.setItem("customerGroups", JSON.stringify(r[1]));
        this._dataService.setLS("customerGroups", r[7]);
        this._dataService.customerGroups.set(r[7]);

        localStorage.setItem("materials", JSON.stringify(r[2]));
        this._dataService.setLS("materials", r[2]);
        this._dataService.materials.set(r[2]);

        localStorage.setItem("stockGroups", JSON.stringify(r[3]));
        this._dataService.setLS("stockGroups", r[3]);
        this._dataService.stockGroups.set(r[3]);

        localStorage.setItem("products", JSON.stringify(r[4]));
        this._dataService.setLS("products", r[4]);
        this._dataService.products.set(r[4]);

        localStorage.setItem("bankGroups", JSON.stringify(r[5]));
        this._dataService.setLS("bankGroups", r[5]);
        this._dataService.bankGroups.set(r[5]);

        localStorage.setItem("cashBox", JSON.stringify(r[6]));
        this._dataService.setLS("cashBox", r[6]);
        this._dataService.cashBox.set(r[6]);

        this._dataService.setLS("todos", r[8]);
        this._dataService.todos.set(r[8]);

        this._dataService.setLS("users", r[9]);
        localStorage.setItem("users", JSON.stringify(r[9]));
        this._dataService.users.set(r[9]);

        this._dataService.setLS("formulas", r[10]);
        this._dataService.formulas.set(r[10]);

        db.formulaGroups.bulkPut(r[0]);
        db.customers.bulkPut(r[1]);
        db.customerGroups.bulkPut(r[7]);
        this._dataService.customerGroups.set(r[7]);

        console.log("Settings Updated Sync Service");
        this.gettingSettings = false;
      },
      error: () => {
        this.offlineSettings();
        this.gettingSettings = false;
      },
    });
  }

  async offlineSettings() {
    let formulaGroups = await db.formulaGroups.toArray();
    this._dataService.formulaGroups.set(formulaGroups);

    let customers = await db.customers.toArray();
    this._dataService.customers.set(customers);

    let customerGroups = await db.customerGroups.toArray();
    this._dataService.customerGroups.set(customerGroups);
  }

  /**
   * Cleanup orphaned temporary records (UUIDs) older than 1 hour
   * These are records where SYNC_SUCCESS message was lost due to browser crash, etc.
   */
  async cleanupStaleTemporaryRecords(): Promise<{
    cleaned: number;
    staleRecords: any[];
  }> {
    try {
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      
      // Find transactions with UUID-style ids (contain '-') that are:
      // 1. Older than 1 hour
      // 2. Not marked as synced
      const staleTransactions = await db.transactions
        .filter(t => {
          const hasUuidId = typeof t.id === 'string' && t.id.includes('-');
          const isOld = t.dateAdded && new Date(t.dateAdded).getTime() < oneHourAgo;
          const notSynced = t.syncStatus !== 'synced';
          return hasUuidId && isOld && notSynced;
        })
        .toArray();
      
      if (staleTransactions.length === 0) {
        console.log('No stale temporary records found');
        return { cleaned: 0, staleRecords: [] };
      }
      
      console.warn(`Found ${staleTransactions.length} stale temporary records`, staleTransactions.map(t => t.id));
      
      let cleaned = 0;
      
      // Delete stale transactions and their related records
      for (const transaction of staleTransactions) {
        try {
          await db.transaction('rw', db.transactions, db.bank, db.sells, db.stock, db.production, async () => {
            // Delete related records
            await db.bank.where('tId').equals(transaction.id).delete();
            await db.sells.where('tId').equals(transaction.id).delete();
            await db.stock.where('tId').equals(transaction.id).delete();
            await db.production.where('tId').equals(transaction.id).delete();
            
            // Delete the transaction itself
            await db.transactions.delete(transaction.id);
            
            console.log(`Cleaned up stale transaction: ${transaction.id}`);
            cleaned++;
          });
        } catch (error) {
          console.error(`Failed to cleanup stale transaction ${transaction.id}:`, error);
        }
      }
      
      return {
        cleaned,
        staleRecords: staleTransactions
      };
    } catch (error) {
      console.error('Error during stale record cleanup:', error);
      return { cleaned: 0, staleRecords: [] };
    }
  }
}
